import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/lib/supabaseClient";
import {
  clearStoredImpersonationSessionId,
  getImpersonationAdminReturnUrl,
  getStoredImpersonationAccessToken,
  getStoredImpersonationSessionId,
  setStoredImpersonationAdminReturnPath,
  setStoredImpersonationAuthTokens,
  setStoredImpersonationSessionContext,
  setStoredImpersonationSessionId,
} from "@/lib/impersonationContext";
import {
  readImpersonationAuthMessageFromHash,
  waitForImpersonationAuthMessage,
} from "@/lib/impersonationAuthMessage";
import { returnToSuperAdmin } from "@/lib/impersonationNavigation";

const ImpersonationContext = createContext(null);
const MISSING_AUTH_DESCRIPTION = "Super Admin auth handoff was missing. Return to Super Admin and try again.";

export function ImpersonationProvider({ children }) {
  const [searchParams] = useSearchParams();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ending, setEnding] = useState(false);

  const refresh = useCallback(async () => {
    const urlSessionId = searchParams.get("session_id");
    if (urlSessionId) {
      clearStoredImpersonationSessionId();
      setStoredImpersonationAdminReturnPath("/");
      const authMessage = readImpersonationAuthMessageFromHash(urlSessionId)
        || (await waitForImpersonationAuthMessage(urlSessionId));
      if (!authMessage) {
        window.history.replaceState({}, "", "/dashboard");
        setSession(null);
        setLoading(false);
        window.dispatchEvent(new Event("dental-os:impersonation-session-ended"));
        toast.error("Could not start impersonation.", { description: MISSING_AUTH_DESCRIPTION });
        return null;
      }
      setStoredImpersonationSessionId(urlSessionId);
      setStoredImpersonationAuthTokens(authMessage);
      setStoredImpersonationAdminReturnPath(authMessage.admin_return_path);
      window.history.replaceState({}, "", "/dashboard");
    }

    const sessionId = urlSessionId || getStoredImpersonationSessionId();
    if (!sessionId) {
      setSession(null);
      setLoading(false);
      return null;
    }

    if (!getStoredImpersonationAccessToken()) {
      clearStoredImpersonationSessionId();
      setSession(null);
      setLoading(false);
      window.dispatchEvent(new Event("dental-os:impersonation-session-ended"));
      toast.error("Impersonation session ended.", { description: MISSING_AUTH_DESCRIPTION });
      return null;
    }

    setLoading(true);
    const { data, error } = await supabase.rpc("get_active_impersonation_session", {
      p_session_id: sessionId,
    });

    if (error || !data?.active) {
      clearStoredImpersonationSessionId();
      setSession(null);
      setLoading(false);
      window.dispatchEvent(new Event("dental-os:impersonation-session-ended"));
      if (error) toast.error("Impersonation session ended.", { description: error.message });
      return null;
    }

    setStoredImpersonationSessionId(data.id);
    setStoredImpersonationSessionContext(data);
    setSession(data);
    setLoading(false);
    window.dispatchEvent(new CustomEvent("dental-os:impersonation-session-started", { detail: data }));
    return data;
  }, [searchParams]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!session?.id) return undefined;
    const timer = window.setInterval(() => {
      setSession((current) => {
        if (!current) return current;
        const next = Math.max(0, Number(current.seconds_remaining || 0) - 1);
        return { ...current, seconds_remaining: next };
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [session?.id]);

  const finishSession = useCallback(async (endReason = "manual") => {
    const sessionId = session?.id || getStoredImpersonationSessionId();
    if (!sessionId || ending) return;
    setEnding(true);
    const { error } = await supabase.rpc("admin_end_impersonation_session", {
      p_session_id: sessionId,
      p_end_reason: endReason,
    });
    setEnding(false);
    if (error && endReason !== "timeout") {
      toast.error("Could not end impersonation.", { description: error.message });
      return;
    }
    const adminReturnUrl = getImpersonationAdminReturnUrl();
    clearStoredImpersonationSessionId();
    setSession(null);
    window.dispatchEvent(new Event("dental-os:impersonation-session-ended"));
    toast.success(endReason === "timeout" ? "Impersonation session expired." : "Impersonation session ended.");
    returnToSuperAdmin(adminReturnUrl);
  }, [ending, session?.id]);

  useEffect(() => {
    if (!session?.id || ending) return;
    if (Number(session.seconds_remaining || 0) <= 0) {
      finishSession("timeout");
    }
  }, [ending, finishSession, session?.id, session?.seconds_remaining]);

  const endSession = useCallback(() => finishSession("manual"), [finishSession]);

  const value = useMemo(() => ({
    session,
    loading,
    ending,
    active: Boolean(session?.active),
    refresh,
    endSession,
  }), [session, loading, ending, refresh, endSession]);

  return <ImpersonationContext.Provider value={value}>{children}</ImpersonationContext.Provider>;
}

export function useImpersonation() {
  const ctx = useContext(ImpersonationContext);
  if (!ctx) throw new Error("useImpersonation must be used inside <ImpersonationProvider>");
  return ctx;
}
